import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, fileUrl, formatApiErrorDetail } from "@/lib/api";
import { RichTextEditor } from "@/components/RichTextEditor";
import { LOGO_URL } from "@/data/homeContent";
import { ArrowLeft, Loader2, Upload, Save, ImageIcon } from "lucide-react";

export default function ListingForm() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();

  const [countryList, setCountryList] = useState([]);
  const [form, setForm] = useState({ school_name: "", country: "", location: "", content: "", image_url: "", image_width: 100, featured: false });
  const [countryMode, setCountryMode] = useState("list"); // list | custom
  const [customCountry, setCustomCountry] = useState("");
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    api.get("/admin/countries").then((res) => setCountryList(res.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (isEdit) {
      api.get("/admin/listings").then((res) => {
        const l = res.data.find((x) => x.id === id);
        if (l) {
          setForm({ school_name: l.school_name, country: l.country, location: l.location, content: l.content, image_url: l.image_url, image_width: l.image_width || 100, featured: l.featured });
        } else {
          toast.error("Listing not found");
          navigate("/admin");
        }
      }).finally(() => setLoading(false));
    }
  }, [id, isEdit, navigate]);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, image_url: data.url }));
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    const country = countryMode === "custom" ? customCountry.trim() : form.country;
    if (!form.school_name.trim()) return toast.error("School name is required");
    if (!country) return toast.error("Please select or enter a country");
    setSaving(true);
    const payload = { ...form, country, location: form.location || country };
    try {
      if (isEdit) {
        await api.put(`/admin/listings/${id}`, payload);
        toast.success("Listing updated");
      } else {
        await api.post("/admin/listings", payload);
        toast.success("Listing created");
      }
      navigate("/admin");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-500"><Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…</div>;

  return (
    <div className="min-h-screen bg-[#faf9f6]" data-testid="admin-listing-form">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-5xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/admin" className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-[#800020]" data-testid="form-back-btn">
            <ArrowLeft className="h-4 w-4" /> Back to Listings
          </Link>
          <img src={LOGO_URL} alt="BSD" className="h-8" />
        </div>
      </header>

      <form onSubmit={submit} className="max-w-5xl mx-auto px-6 py-8">
        <h1 className="font-serif text-3xl text-slate-900 mb-6">{isEdit ? "Edit Listing" : "New School Listing"}</h1>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Left column: fields */}
          <div className="lg:col-span-2 space-y-5">
            <div className="bg-white rounded-xl border border-slate-200 p-6 space-y-5">
              <label className="block">
                <span className="block text-sm font-medium text-slate-700 mb-1.5">School Name *</span>
                <input value={form.school_name} onChange={(e) => setForm({ ...form, school_name: e.target.value })} className="w-full border border-slate-300 rounded-lg px-3.5 py-2.5 text-sm outline-none focus:border-[#800020]" data-testid="admin-school-title-input" />
              </label>

              <div className="grid sm:grid-cols-2 gap-5">
                <label className="block">
                  <span className="block text-sm font-medium text-slate-700 mb-1.5">Country *</span>
                  {countryMode === "list" ? (
                    <select value={form.country} onChange={(e) => e.target.value === "__custom" ? setCountryMode("custom") : setForm({ ...form, country: e.target.value })} className="w-full border border-slate-300 rounded-lg px-3.5 py-2.5 text-sm outline-none focus:border-[#800020]" data-testid="admin-country-select">
                      <option value="">Select country…</option>
                      {countryList.map((c) => <option key={c} value={c}>{c}</option>)}
                      <option value="__custom">+ Add new country…</option>
                    </select>
                  ) : (
                    <div className="flex gap-2">
                      <input value={customCountry} onChange={(e) => setCustomCountry(e.target.value)} placeholder="New country name" className="w-full border border-slate-300 rounded-lg px-3.5 py-2.5 text-sm outline-none focus:border-[#800020]" data-testid="admin-country-custom" />
                      <button type="button" onClick={() => { setCountryMode("list"); setCustomCountry(""); }} className="text-xs text-slate-500 whitespace-nowrap">Use list</button>
                    </div>
                  )}
                </label>
                <label className="block">
                  <span className="block text-sm font-medium text-slate-700 mb-1.5">Location / City</span>
                  <input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="w-full border border-slate-300 rounded-lg px-3.5 py-2.5 text-sm outline-none focus:border-[#800020]" data-testid="admin-location-input" />
                </label>
              </div>
            </div>

            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <span className="block text-sm font-medium text-slate-700 mb-2">Listing Content</span>
              <RichTextEditor value={form.content} onChange={(html) => setForm((f) => ({ ...f, content: html }))} />
              <p className="text-xs text-slate-400 mt-2">This appears below the image on the listing page, as on the reference layout.</p>
            </div>
          </div>

          {/* Right column: image + options */}
          <div className="space-y-5">
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <span className="block text-sm font-medium text-slate-700 mb-3">Listing Image</span>
              <div className="aspect-video rounded-lg border border-dashed border-slate-300 bg-slate-50 overflow-hidden flex items-center justify-center mb-3">
                {form.image_url ? (
                  <img src={fileUrl(form.image_url)} alt="preview" className="h-full w-full object-cover" data-testid="admin-image-preview" />
                ) : (
                  <ImageIcon className="h-8 w-8 text-slate-300" />
                )}
              </div>
              <label className="inline-flex items-center justify-center gap-2 w-full px-4 py-2.5 rounded-lg border border-slate-300 text-sm font-medium text-slate-700 hover:border-[#800020] cursor-pointer transition-colors" data-testid="admin-image-upload-label">
                {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                {uploading ? "Uploading…" : "Upload Image"}
                <input type="file" accept="image/*" onChange={upload} className="hidden" data-testid="admin-image-input" />
              </label>
              <div className="mt-3">
                <span className="block text-xs text-slate-400 mb-1">or paste image URL</span>
                <input value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} placeholder="https://…" className="w-full border border-slate-300 rounded-lg px-3 py-2 text-xs outline-none focus:border-[#800020]" data-testid="admin-image-url-input" />
              </div>
              <div className="mt-3">
                <span className="block text-xs text-slate-400 mb-1">Image width on listing page</span>
                <div className="flex items-center gap-3">
                  <input type="range" min="20" max="100" step="5" value={Number(form.image_width) || 100} onChange={(e) => setForm({ ...form, image_width: Number(e.target.value) })} className="flex-1 accent-[#800020]" data-testid="admin-image-width-range" />
                  <span className="w-12 text-right text-xs text-slate-600" data-testid="admin-image-width-value">{Number(form.image_width) || 100}%</span>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <label className="flex items-center gap-3 cursor-pointer">
                <input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} className="h-4 w-4 accent-[#800020]" data-testid="admin-featured-checkbox" />
                <span className="text-sm text-slate-700">Mark as Featured</span>
              </label>
            </div>

            <button type="submit" disabled={saving} className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg bg-[#800020] hover:bg-[#660019] text-white font-medium transition-colors disabled:opacity-60" data-testid="admin-save-school-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              {isEdit ? "Update Listing" : "Create Listing"}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
